// Cliente del flujo "Generar nuevos angulos" (alias "carrusel") para el
// Catálogo de muebles. Con el flag va al atlas (/v1/generate/angles, vía el
// cliente migrado de lib/generateAngles); el fallback n8n es el workflow
// CVct2q0l0Q5g1HOl.
//
// QUÉ HACE, que no es obvio por el nombre: NO genera N imágenes de cero. Parte
// de UNA imagen YA GENERADA (`image_id`) y un agente Gemini escribe N prompts
// que mantienen el mismo sujeto y el mismo entorno variando ángulo, pose y
// encuadre. Por eso sirve para "más fotos del mismo mueble en el mismo
// ambiente" y no para "más ideas distintas".
//
// Hasta ahora solo se llamaba desde `GeneratedImagesInline` /
// `GeneratedImagesHistory`, con la imagen que el user tiene abierta. El modo
// Catálogo de muebles lo necesita en lote (una imagen por producto), así que la
// llamada se extrajo acá.

import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../config';
import { postGenerateAnglesFrom } from './generateAngles';
import type { Medidas } from './medidas';

const NUEVOS_ANGULOS_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/generar_nuevos_angulos';

export interface NuevosAngulosParams {
  userId: string;
  /** Id de la fila de `imagenes_generadas` que se usa como referencia. */
  imageId: string;
  /** Cuántas imágenes NUEVAS pedir (no incluye la original). */
  cantidad: number;
  /**
   * Medidas del producto, para que las tomas alternativas no distorsionen la
   * escala.
   *
   * ⚠️ HOY n8n IGNORA ESTOS CAMPOS: el `<carousel_prompt_generator>` no los
   * lee. Se mandan igual porque son inertes (campos de más en el body) y
   * porque el fix del lado de n8n es justamente inyectarlos en ese prompt —
   * cuando se haga, el front ya los está enviando. Ver ADR-584.
   */
  medidas?: Medidas | null;
  proporcion?: string | null;
}

/**
 * Pide tomas alternativas de una imagen ya generada.
 *
 * Devuelve los ids de `imagenes_generadas` que creó, o `[]` si algo falló. NO
 * tira excepción a propósito: el caller ya tiene las imágenes base en la mano y
 * un fallo acá no debe tumbar toda la generación.
 *
 * ⚠️ TOPE PRÁCTICO (solo fallback n8n, flag OFF): `cantidad` > 2 hace que el
 * workflow loopee hasta su timeout de 180s. Su nodo `get gen images3` tiene
 * `limit: 2` hardcodeado mientras la condición de corte exige
 * `>= cantidad_imagenes`, así que nunca se cumple. El atlas no tiene ese bug
 * (acepta hasta 6), pero mientras el flag exista como rollback los callers
 * conviene que sigan limitando a 2.
 */
export async function pedirNuevosAngulos({
  userId,
  imageId,
  cantidad,
  medidas,
  proporcion,
}: NuevosAngulosParams): Promise<string[]> {
  // Ruteo al backend nuevo: es el MISMO workflow que "nuevos ángulos"
  // (CVct2q0l0Q5g1HOl), ya migrado a /v1/generate/angles (ADR-600) — este
  // helper era el último cliente que seguía pegándole a n8n
  // (ADR-2026-08-24-carrusel-muebles-al-atlas). Se delega en el cliente
  // migrado; `medidas`/`proporcion` NO viajan al atlas porque su schema no
  // los modela y n8n también los ignoraba (ADR-584 — si se retoma ese fix,
  // ahora va del lado del atlas: schema + prompt del carrusel).
  if (USE_ATLAS_BACKEND) {
    try {
      return await postGenerateAnglesFrom(
        { tipo: 'generada', imageId },
        { userId, cantidad },
      );
    } catch (e) {
      // Contrato de este helper: NUNCA tira (un fallo no debe tumbar la tanda
      // del catálogo — el caller usa allSettled y entrega las bases igual).
      // Cubre también el 402 tipado del atlas: ese producto queda sin extras.
      console.warn('[carrusel] atlas falló', e);
      return [];
    }
  }
  try {
    const res = await fetch(NUEVOS_ANGULOS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify({
        user_id: userId,
        image_id: imageId,
        cantidad_imagenes: cantidad,
        ...(medidas ? { medidas } : {}),
        ...(proporcion ? { proporcion } : {}),
      }),
    });
    if (!res.ok) {
      console.warn('[carrusel] respuesta no OK', res.status);
      return [];
    }
    const data = await res.json();
    // El webhook responde a veces como objeto y a veces envuelto en array
    // (mismo doble formato que ya contempla GeneratedImagesInline).
    const ids =
      (Array.isArray(data) ? data[0]?.imagenes_generadas_ids : null) ??
      data?.imagenes_generadas_ids;
    return Array.isArray(ids) ? ids.map((v: unknown) => String(v)) : [];
  } catch (e) {
    console.warn('[carrusel] falló la llamada', e);
    return [];
  }
}
